import { DoubleRightOutlined } from '@ant-design/icons';
import { Button, Form, FormTitle, InputNumber, Wrapper, message, useBreadcrumbRoutes } from '@maxtropy/components';
import {
  apiV2LoadDispatchCenterConfigDetailPost,
  apiV2LoadDispatchCenterConfigFurnaceListPost,
  apiV2LoadDispatchCenterConfigSavePost,
} from '@maxtropy/dmes-apis-v2';
import type { V2LoadDispatchCenterConfigSavePostRequest } from '@maxtropy/dmes-apis-v2';
import { useRequest } from 'ahooks';
import { useNavigate } from 'react-router-dom';
import type { FurnaceItem, FurnaceType } from './components/FurnaceSelector';
import StrategySection from './components/StrategySection';
import styles from './index.module.scss';

interface ModeConfigFormValues {
  reduceMode: number;
  reduceCustomMethod: number;
  reduceLoadObjectIds: number[];
  increaseMode: number;
  increaseCustomMethod: number;
  increaseLoadObjectIds: number[];
  advanceMinutes?: number;
}

const initialValues: ModeConfigFormValues = {
  reduceMode: 1,
  reduceCustomMethod: 1,
  reduceLoadObjectIds: [],
  increaseMode: 1,
  increaseCustomMethod: 1,
  increaseLoadObjectIds: [],
};

const getFurnaceType = (deviceType?: string): FurnaceType =>
  deviceType?.includes('电石') ? 'calcium-carbide' : 'ferrosilicon';

const LoadDispatchModeConfig = () => {
  const routesContext = useBreadcrumbRoutes();
  const navigate = useNavigate();
  const [form] = Form.useForm<ModeConfigFormValues>();
  const { data: furnaces = [], loading: furnacesLoading } = useRequest(() =>
    apiV2LoadDispatchCenterConfigFurnaceListPost({}).then(response =>
      (response.furnaceList ?? []).flatMap<FurnaceItem>(item =>
        item.loadObjectId == null
          ? []
          : [
              {
                id: item.loadObjectId,
                name: item.deviceName ?? '--',
                code: item.deviceCode ?? '--',
                type: getFurnaceType(item.deviceType),
              },
            ]
      )
    )
  );
  const { loading: detailLoading } = useRequest(() =>
    apiV2LoadDispatchCenterConfigDetailPost({}).then(detail => {
      form.setFieldsValue({
        reduceMode: detail.dropMode ?? initialValues.reduceMode,
        reduceCustomMethod: detail.dropCustomType ?? initialValues.reduceCustomMethod,
        reduceLoadObjectIds: detail.dropLoadObjectIds ?? [],
        increaseMode: detail.raiseMode ?? initialValues.increaseMode,
        increaseCustomMethod: detail.raiseCustomType ?? initialValues.increaseCustomMethod,
        increaseLoadObjectIds: detail.raiseLoadObjectIds ?? [],
        advanceMinutes: detail.advanceMinutes,
      });
    })
  );
  const { runAsync: saveConfig, loading: saving } = useRequest(
    (requestData: V2LoadDispatchCenterConfigSavePostRequest) => apiV2LoadDispatchCenterConfigSavePost(requestData),
    { manual: true }
  );

  const handleSave = async (values: ModeConfigFormValues) => {
    await saveConfig({
      dropMode: values.reduceMode,
      dropCustomType: values.reduceMode === 2 ? values.reduceCustomMethod : undefined,
      dropLoadObjectIds: values.reduceMode === 2 && values.reduceCustomMethod === 1 ? values.reduceLoadObjectIds : [],
      raiseMode: values.increaseMode,
      raiseCustomType: values.increaseMode === 2 ? values.increaseCustomMethod : undefined,
      raiseLoadObjectIds:
        values.increaseMode === 2 && values.increaseCustomMethod === 1 ? values.increaseLoadObjectIds : [],
      advanceMinutes: values.advanceMinutes,
    });
    message.success('保存成功');
  };

  const handleCancel = () => navigate('/dispatch/control/load-center');

  return (
    <Wrapper routes={[...(routesContext?.routes ?? []), { name: '调控模式配置' }]} hasPadding={false}>
      <main className={styles.page}>
        <FormTitle
          className={styles.pageTitle}
          title="调控模式配置"
          extraContent={
            <Button
              type="link"
              className={styles.operationRecordButton}
              href="/dispatch/controlLoadCenter/operation-record"
            >
              <span>调控策略操作记录</span>
              <DoubleRightOutlined />
            </Button>
          }
        />

        <Form
          form={form}
          className={styles.configForm}
          initialValues={initialValues}
          disabled={detailLoading}
          layout="horizontal"
          labelAlign="left"
          labelCol={{ flex: '200px' }}
          wrapperCol={{ flex: 'auto' }}
          colon={false}
          onFinish={handleSave}
        >
          <StrategySection type="reduce" furnaces={furnaces} furnacesLoading={furnacesLoading} />
          <StrategySection type="increase" furnaces={furnaces} furnacesLoading={furnacesLoading} />

          <Form.Item
            label="调度建议提前时长"
            name="advanceMinutes"
            rules={[
              { required: true, message: '请输入调度建议提前时长' },
              { type: 'number', min: 1, max: 99999999, message: '请输入1~99999999的整数' },
            ]}
          >
            <InputNumber
              className={styles.durationInput}
              min={1}
              max={99999999}
              precision={0}
              placeholder="请输入"
              addonAfter="分钟"
            />
          </Form.Item>

          <div className={styles.formActions}>
            <Button type="primary" htmlType="submit" loading={saving}>
              保存
            </Button>
            <Button htmlType="button" onClick={handleCancel}>
              取消
            </Button>
          </div>
        </Form>
      </main>
    </Wrapper>
  );
};

export default LoadDispatchModeConfig;
